import * as d3 from "d3";

// Based on:
// Copyright 2021-2023 Observable, Inc.
// Released under the ISC license.
// https://observablehq.com/@d3/tree
export function AppDependencyTree(
    data,
    {
        // data is either tabular (array of objects) or hierarchy (nested objects)
        tree = d3.tree, // layout algorithm (typically d3.tree or d3.cluster)
        sort, // how to sort nodes prior to layout (e.g., (a, b) => d3.descending(a.height, b.height))
        label, // given a node d, returns the display name
        title, // given a node d, returns its hover text
        link, // given a node d, its link (if any)
        width = 640, // outer width, in pixels
        height, // outer height, in pixels
        r = 3, // radius of nodes
        padding = 20, // horizontal padding for first and last column
        rootColor = "#007bff", // boostrap primary
        fill = "#999", // fill for nodes
        stroke = "#555", // stroke for links
        strokeWidth = 1.5, // stroke width for links
        strokeOpacity = 0.4, // stroke opacity for links
        strokeLinejoin, // stroke line join for links
        strokeLinecap, // stroke line cap for links
        halo = "#fff", // color of label halo
        haloWidth = 3, // padding around the labels
        curve = d3.curveBumpX, // curve for the link
    } = {},
) {
    const childRoot = d3.hierarchy(data, (d) => d.children);
    const parentRoot = d3.hierarchy(data, (d) => d.parents);
    const rootId = data.id;

    // Sort the nodes.
    if (sort != null) {
        childRoot.sort(sort);
        parentRoot.sort(sort);
    }

    // Compute the layout.
    const dx = 30;
    const dy =
        (width - 2 * padding) /
        Math.max(1, parentRoot.height + childRoot.height);
    tree().nodeSize([dx, dy])(childRoot);
    tree().nodeSize([dx, dy])(parentRoot);

    parentRoot.each((d) => {
        d.y = -d.y;
    });

    // Center the tree.
    let x0 = Infinity;
    let x1 = -x0;
    let y0 = Infinity;
    let y1 = -y0;
    [childRoot, parentRoot].forEach((root) => {
        root.each((d) => {
            if (d.x > x1) x1 = d.x;
            if (d.x < x0) x0 = d.x;
            if (d.y > y1) y1 = d.y;
            if (d.y < y0) y0 = d.y;
        });
    });

    // Compute the default height.
    if (height === undefined) height = x1 - x0 + dx * 2;

    // Use the required curve
    if (typeof curve !== "function") throw new Error(`Unsupported curve`);

    const svg = d3
        .create("svg")
        .attr("viewBox", [y0 - padding, x0 - dx, y1 - y0 + 2 * padding, height])
        .attr("width", width)
        .attr("height", height)
        .attr("style", "max-width: 100%; height: auto; height: intrinsic;")
        .attr("font-family", "Maven pro")
        .attr("font-size", 10);

    svg.append("g")
        .attr("fill", "none")
        .attr("stroke", stroke)
        .attr("stroke-opacity", strokeOpacity)
        .attr("stroke-linecap", strokeLinecap)
        .attr("stroke-linejoin", strokeLinejoin)
        .attr("stroke-width", strokeWidth)
        .selectAll("path")
        .data([...childRoot.links(), ...parentRoot.links()])
        .join("path")
        .attr(
            "d",
            d3
                .link(curve)
                .x((d) => d.y)
                .y((d) => d.x),
        );

    const node = svg
        .append("g")
        .selectAll("g.node")
        .data([
            ...childRoot.descendants(),
            ...parentRoot.descendants().filter((d) => d.depth !== 0),
        ])
        .join("g")
        .attr("class", "node")
        .attr("transform", (d) => `translate(${d.y},${d.x})`)
        .style("cursor", link ? "pointer" : "default")
        .on("click", (event, d) => {
            event.preventDefault();
            event.stopPropagation();
            link?.(d.data, d);
        });

    node.append("circle")
        .attr("fill", (d) =>
            d.data.id === rootId ? rootColor : d.children ? stroke : fill,
        )
        .attr("r", r);

    if (title != null) node.append("title").text((d) => title(d.data, d));

    if (label != null)
        node.append("text")
            .attr("dy", "-0.5em")
            .attr("x", 0)
            .attr("text-anchor", "middle")
            .attr("paint-order", "stroke")
            .attr("stroke", halo)
            .attr("stroke-width", haloWidth)
            .attr("font-size", "1.3em")
            .attr("font-weight", (d) => (d.data.id === rootId ? "bold" : null))
            .attr("fill", (d) => (d.data.id === rootId ? rootColor : null))
            .text((d) => label(d.data, d));

    return svg.node();
}
